import React from 'react';
import { FileStack } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CollapsibleCard, CollapsibleCardContent } from 'src/components/ui/collapsible-card';
import { useToggle } from 'src/lib/state-utils';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { Label } from 'src/components/ui/label';
import { requestFlow } from 'src/lib/services/api/request-api';

/**
 * Project strategy card for the CLIENT Summary tab — shows the project strategy
 * label ("Customized" / "Editor's selection"), mirroring the admin/editor
 * "Multiple outcomes" card so the two views read the same (production parity).
 * Reads request.isEditorsChoice / isCustom (derived from outcome_type in
 * decorate-request).
 *
 * INTENTIONAL DIVERGENCE FROM ADMIN: the admin card carries an editor-facing
 * description under each label ("Look over the client's content…", "Check the
 * pending tasks and edit them in the order requested by the client"). Those are
 * instructions written FOR the editor and read oddly on the client's own
 * Summary tab, so the client card is deliberately label-only. This is not a
 * shared component because admin-frontend and customer-frontend are separate
 * repos with no shared package; keep the label i18n keys in sync by hand.
 */
const RequestStrategyCard: React.FC<React.ComponentProps<typeof CollapsibleCard>> = (props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useToggle(true);
  const request = useDecoratedRequestContext();

  // Strategy applies ONLY to the genuine multi_outcome flow with a RESOLVED
  // strategy AND actual outcomes — gating on `with_multiple_outcomes` alone is
  // the wrong signal (#1097 review):
  //  - REPURPOSE projects route through this same page and the backend treats
  //    them as multi-outcome (isMultiOutcomeFlow() = MultiOutcome || Repurpose),
  //    so the API returns multiple_outcomes.outcome_type for them too — and the
  //    repurpose form seeds outcome_type='editor_choice'. Gating on outcome_type
  //    alone would therefore wrongly show a strategy card on repurpose, so we
  //    must gate on the flow itself (flow === multi_outcome).
  //  - A fresh or partially-hydrated parent can have the flow set with no
  //    outcomes yet; gating on outcomes.length keeps this consistent with the
  //    Outcomes summary card, so a strategy never renders above an empty list.
  //  - An unresolved / absent outcome_type renders nothing rather than silently
  //    defaulting to "Customized".
  const isMultiOutcomeFlow = request.flow === requestFlow.multiOutcome;
  const hasOutcomes = !!request.multiple_outcomes?.outcomes?.length;
  if (!isMultiOutcomeFlow || !hasOutcomes || (!request.isEditorsChoice && !request.isCustom)) {
    return null;
  }

  return (
    <CollapsibleCard
      icon={<FileStack />}
      title={t('attributes/request:multiple_outcomes', { defaultValue: 'Multiple outcomes' })!}
      open={open}
      onOpenChange={setOpen}
      variant={'shadow'}
      {...props}
    >
      <CollapsibleCardContent className={'tw-flex tw-flex-col tw-gap-1 tw-py-4'}>
        <Label size={'lg'} className={'tw-mb-0'}>
          {request.isEditorsChoice
            ? t('attributes/request:editor_selection', { defaultValue: `Editor's selection` })
            : t('attributes/request:customized', { defaultValue: 'Customized' })}
        </Label>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};

export { RequestStrategyCard };
